import { Link, useHistory } from "react-router-dom";
import React, { FormEvent, useEffect, useState } from "react";
import { BUSINESS_CATEGORIES } from "src/constants/businessCategories";
import { Box } from "src/components/Box";
import { Breakpoint } from "src/components/Breakpoint";
import { ModalManager } from "src/components/Modal";
import { useErrorModalStore } from "src/components/ErrorModal";
import { LegacyBusinessInfoCorporateForm } from "./LegacyBusinessInfoCorporateForm";
import { Button } from "src/components/Button";
import Checkbox from "@asphalt-react/checkbox";
import { ComboBox } from "src/components/ComboBox";
import { DEV_PARTNERS } from "src/constants/devPartners";
import { ENTITY_TYPES } from "src/constants/entityTypes";
import { Input } from "src/components/Input";
import { LoadingOverlay } from "src/components/LoadingOverlay";
import { PASSPORT_ROUTES } from "src/constants/routes";
import { Text } from "src/components/Text";
import TextField from "@asphalt-react/textfield";
import { TooltipModal } from "../BusinessInfo.tooltip";
import { observer } from "mobx-react-lite";
import { useBusinessInfoState } from "./LegacyBusinessInfoState";
import { usePassportStore } from "src/stores/Passport.store";
import { eventDispatcher } from "src/helpers/eventEmitter";
import { usePassportImprovements, useTranslation } from "src/hooks";
import {
  setCookie,
  CookieNames,
  getBoolCookie,
  setBoolCookie,
} from "src/helpers/cookies";
import { Logger } from "src/helpers";
import { errFormatter } from "src/helpers/errorFormatter";
import { Info } from "@asphalt-react/iconpack";
import { AlertBox } from "src/components/AlertBox";
import { Dropdown } from "src/components/Dropdown";
import { TextArea } from "src/components";
import {
  CheckboxContainerEl,
  BoxRowEl,
  GridContainerEl,
  EntityTypeContainerEl,
  SecondInputContainerEl,
  DividerEl,
  LabelEl,
  TextFieldContainerEl,
  AdditionalInfoContainerEl,
  BoldDividerEl,
} from "../BusinessInfo.styled";

const businessCategoryOptions = BUSINESS_CATEGORIES.map((i) => ({
  id: i.name,
  key: i.name,
  code: i.code,
}));

const devPartnersOptions = DEV_PARTNERS;

export const LegacyBusinessInfoForm = observer(() => {
  const [showModal, setShowModal] = useState(false);
  const [businessCategoryClicked, setBusinessCategoryClicked] = useState(false);
  const [isModalShown, setIsModalShown] = useState(
    getBoolCookie(CookieNames.PassportBusinessInfoUrl)
  );
  const [postalCodeClicked, setPostalCodeClicked] = useState(false);
  const translate = useTranslation();
  const { push } = useHistory();
  const {
    outlet,
    isCorporate,
    isCardPayment,
    selectedBusinessType,
  } = usePassportStore();
  const store = usePassportStore();
  const state = useBusinessInfoState();
  const {
    shipping,
    websiteOption,
    linksOption,
    isShowUrlInput,
    isShowAdditionalInfo,
    isShowWebGuideText,
    form,
    setValue,
    showTooltip,
    shouldDisplayDevPartner,
    fillForm,
    postalComboBoxOptions,
    validateInput,
  } = state;
  const errorModalStore = useErrorModalStore();
  const {
    isImprovementEnabled,
    enableImprovementsAndRedirect,
  } = usePassportImprovements();

  useEffect(() => {
    eventDispatcher.publish({ action: "ob_business_info_opened" });
  }, []);

  useEffect(() => {
    fillForm(store.outlet, store.authUser);
  }, [fillForm, store.authUser, store.outlet]);

  useEffect(() => {
    if (!isModalShown && showTooltip) {
      setShowModal(true);
      setIsModalShown(true);
      setBoolCookie(CookieNames.PassportBusinessInfoUrl, true);
    }
  }, [setShowModal, setIsModalShown, state, showTooltip, isModalShown]);

  const getWebsiteOption = () => {
    if (outlet.json.url) {
      if (
        !(
          outlet.json.development_url ||
          outlet.json.social_media_url ||
          outlet.json.marketplace_url
        )
      ) {
        return "yes";
      } else {
        return "in_dev";
      }
    } else {
      if (outlet.json.social_media_url || outlet.json.marketplace_url) {
        return "no";
      }
    }
    return "";
  };

  const websiteOptions = [
    {
      id: "yes",
      key: `${translate("business_info.website.yes")}`,
    },
    {
      id: "in_dev",
      key: `${translate("business_info.website.on_development")}`,
    },
    {
      id: "no",
      key: `${translate("business_info.website.no")}`,
    },
  ];

  const linkOptions = [
    {
      id: "social_media_url",
      key: `${translate("business_info.social_media")}`,
    },
    {
      id: "marketplace_url",
      key: `${translate("business_info.marketplace")}`,
    },
  ];

  const webInDevelopmentOptions = [
    {
      id: "development_url",
      key: `${translate("business_info.options.dev_url")}`,
    },
    {
      id: "social_media_url",
      key: `${translate("business_info.options.social_media_url")}`,
    },
    {
      id: "marketplace_url",
      key: `${translate("business_info.options.marketplace_url")}`,
    },
  ];

  const handleSubmit = async (event: FormEvent) => {
    event?.preventDefault();
    try {
      eventDispatcher.publish({ action: "ob_business_info_next" });

      if (!isImprovementEnabled) {
        await enableImprovementsAndRedirect();
        return;
      }

      await store.updateOutlet(state.toJSON());
      setCookie(CookieNames.PassportStep, PASSPORT_ROUTES.bankDetails);
      push(PASSPORT_ROUTES.bankDetails);
    } catch (err) {
      Logger.error(err as Error);
      const { status, message } = errFormatter(err);
      eventDispatcher.publish({
        action: "ob_business_info_error",
        payload: {
          message: message,
          httpStatus: status,
        },
      });
      errorModalStore.showAlert();
      errorModalStore.retryCta = handleSubmit;
    }
  };

  const getShippingArea = (): string => {
    if (
      outlet.json?.shipping_area &&
      outlet.json.shipping_area !== "Indonesia"
    ) {
      return "other";
    }
    return "indonesia";
  };

  const getLinkOptionPlaceholder = (): string => {
    let placeholder = "";

    if (linksOption === "development_url")
      placeholder = translate("business_info.input.placeholder.staging_url");
    if (linksOption === "marketplace_url")
      placeholder = translate("business_info.input.placeholder.marketplace");
    if (linksOption === "social_media_url")
      placeholder = translate("business_info.input.placeholder.instagram");

    return placeholder;
  };

  const shippingOptions = [
    {
      id: "indonesia",
      key: `${translate("business_info.shipping.indonesia")}`,
    },
    {
      id: "other",
      key: `${translate("business_info.shipping.other")}`,
    },
  ];

  const entityTypesOptions = ENTITY_TYPES.map((i) => ({
    key: i,
    id: i,
  }));

  const postalFromOutlet =
    outlet.json.outlet_city &&
    outlet.json.outlet_postal_code &&
    outlet.json.outlet_province &&
    `${outlet.json.outlet_postal_code} - ${outlet.json.outlet_city} - ${outlet.json.outlet_province}`;

  return (
    <>
      <Box as="form" noValidate onSubmit={handleSubmit}>
        <Breakpoint medium up>
          <Text marginBottomXl fontSize="xlarge" fontWeight="demi">
            {translate("business_info.title")}
          </Text>
        </Breakpoint>
        {isCorporate && (
          <Box marginBottomXl>
            <AlertBox color="info" icon={<Info />}>
              {translate(`business_info.${selectedBusinessType}.alert`)}
            </AlertBox>
          </Box>
        )}
        {shouldDisplayDevPartner && (
          <>
            <CheckboxContainerEl flex alignItemsCenter>
              <Checkbox
                checked={state.withDevPartner}
                onChange={() => (state.withDevPartner = !state.withDevPartner)}
                id="checkbox-dev-partner"
              />
              <Text
                fontSize="medium"
                marginLeftS
                fontWeight="demi"
                as="label"
                htmlFor="checkbox-dev-partner"
              >
                {translate("business_info.checkbox.description")}
              </Text>
            </CheckboxContainerEl>

            {state.withDevPartner && (
              <BoxRowEl>
                <ComboBox
                  options={devPartnersOptions}
                  placeholder={translate(
                    "business_info.placeholder.dev_partner"
                  )}
                  label={translate("business_info.label.dev_partner")}
                  onChange={(option: any) =>
                    setValue("partner_id", option.value)
                  }
                  defaultValue={
                    form.partner_id! || outlet.json.partner_id?.toString()
                  }
                />
              </BoxRowEl>
            )}
          </>
        )}

        <Input
          readOnly
          label={translate("business_info.label.brand_name")}
          defaultValue={outlet.name}
        />
        <GridContainerEl>
          <Box marginBottomL>
            <Dropdown
              typeahead
              name="selectedPostalComboBox"
              items={postalComboBoxOptions}
              initialSelectedItem={postalComboBoxOptions.find(
                (option) =>
                  option.id === state.selectedPostalComboBoxOptions ||
                  option.id === postalFromOutlet
              )}
              onChange={(option: any) => {
                setPostalCodeClicked(true);
                state.applySelectedComboBox(option?.id);
              }}
              label={translate(
                `owner_info.${selectedBusinessType}.postal_code_city_province.label`
              )}
              placeholder={translate(
                `owner_info.${selectedBusinessType}.postal_code_city_province.placeholder`
              )}
              /*TODO: Refactor @sendy.suryana */
              error={postalCodeClicked && !state.form.outlet_city}
              helperText={
                postalCodeClicked && !state.form.outlet_city
                  ? translate("generic.input.error.required")
                  : ""
              }
              size="m"
            />
          </Box>
          <Box marginBottomL>
            <TextArea
              disabledResize
              label={translate(
                `owner_info.${selectedBusinessType}.address.label`
              )}
              placeholder={translate(
                `owner_info.${selectedBusinessType}.address.placeholder`
              )}
              onChange={(e) => setValue("outlet_address", e.target.value)}
              height={40}
              onBlur={() => validateInput("outlet_address", "outlet_address")}
              error={state.inputErrors.outlet_address.isError}
              helperText={
                state.inputErrors.outlet_address.isError
                  ? translate(state.inputErrors.finance_email.text)
                  : ""
              }
              defaultValue={outlet.outletAddress}
              maxLength={255}
              required
            />
          </Box>
        </GridContainerEl>
        <GridContainerEl>
          {isCorporate && (
            <EntityTypeContainerEl>
              <Dropdown
                items={entityTypesOptions}
                initialSelectedItem={entityTypesOptions.find(
                  (option) =>
                    option.id === form.entity_type ||
                    option.id ===
                      state.parseEntityType(outlet.json.official_name)
                )}
                placeholder={translate("business_info.placeholder.entity_type")}
                label={translate("business_info.label.entity_type")}
                onChange={(option: any) => setValue("entity_type", option.id)}
                size="m"
              />
            </EntityTypeContainerEl>
          )}
          <Dropdown
            items={businessCategoryOptions}
            placeholder={translate(
              "business_info.placeholder.business_category"
            )}
            label={translate("business_info.label.business_category")}
            onChange={(option: any) => {
              setBusinessCategoryClicked(true);
              setValue("business_category", option?.id);
              setValue("aspi", { mcc: option?.code });
            }}
            initialSelectedItem={businessCategoryOptions.find(
              (option) =>
                option.id === form.business_category ||
                option.id === outlet?.json?.business_categories?.[0]
            )}
            /*TODO: Refactor @sendy.suryana */
            error={!form.business_category && businessCategoryClicked}
            helperText={
              !form.business_category && businessCategoryClicked
                ? translate("generic.input.error.required")
                : ""
            }
            size="m"
            typeahead
          />
          <Input
            label={translate("business_info.label.business_pic_email")}
            placeholder={translate(
              "business_info.placeholder.business_pic_email"
            )}
            onChange={(option: any) => {
              setValue("email", option.target.value);
              state.inputErrors.email.isError &&
                state.validateInput("email", "email");
            }}
            defaultValue={
              form.email ||
              outlet.json.notification_settings?.email_support ||
              outlet.email
            }
            onBlur={() => state.validateInput("email", "email")}
            error={state.inputErrors.email.isError}
            helperText={
              state.inputErrors.email.isError
                ? translate(state.inputErrors.email.text)
                : ""
            }
            type="email"
          />
          <Input
            label={translate("business_info.label.finance_pic_email")}
            placeholder={translate(
              "business_info.placeholder.finance_pic_email"
            )}
            onChange={(option: any) => {
              setValue("finance_email", option.target.value);
              state.inputErrors.finance_email.isError &&
                state.validateInput("finance_email", "finance_email");
            }}
            defaultValue={
              form.finance_email ||
              outlet.json.notification_settings?.email_finance
            }
            onBlur={() => state.validateInput("finance_email", "finance_email")}
            error={state.inputErrors.finance_email.isError}
            tooltipLabel={translate(
              "business_info.helper.tooltip.finance_pic_email"
            )}
            helperText={
              state.inputErrors.finance_email.isError
                ? translate(state.inputErrors.finance_email.text)
                : ""
            }
            type="email"
          />
        </GridContainerEl>
        {isCorporate && <LegacyBusinessInfoCorporateForm />}
        {isCardPayment && (
          <>
            <BoxRowEl>
              <Box marginBottomL>
                <Dropdown
                  items={shippingOptions}
                  initialSelectedItem={shippingOptions.find(
                    (option) => option.id === getShippingArea()
                  )}
                  placeholder={translate("business_info.placeholder.shipping")}
                  label={translate("business_info.label.shipping")}
                  onChange={(event: any) => state.setShipping(event.id)}
                  size="m"
                />
              </Box>
              {shipping === "other" && (
                <SecondInputContainerEl marginBottomL>
                  <Input
                    placeholder={translate(
                      "business_info.placeholder.shipping_other"
                    )}
                    onChange={(event) =>
                      setValue("shipping_area", event.target.value)
                    }
                    defaultValue={form.shipping_area}
                    required
                  />
                </SecondInputContainerEl>
              )}
            </BoxRowEl>
            <DividerEl />
          </>
        )}
        <BoxRowEl>
          <Box marginBottomL>
            <Dropdown
              placeholder={translate("business_info.placeholder.website")}
              label={translate("business_info.label.website")}
              items={websiteOptions}
              initialSelectedItem={websiteOptions.find(
                (option) =>
                  option.id === websiteOption ||
                  option.id === getWebsiteOption()
              )}
              onChange={(event: any) => {
                state.websiteOption = event.id;
                state.resetWebFilling();
                state.resetWebOption();
              }}
              size="m"
            />
          </Box>
          {isShowUrlInput && (
            <SecondInputContainerEl
              marginBottomL={!state.inputErrors.url.isError}
            >
              <Input
                placeholder={translate(
                  "business_info.input.placeholder.website"
                )}
                type="url"
                onChange={(event) => {
                  setValue("url", event.target.value);
                  state.inputErrors.url.isError &&
                    state.validateInput("url", "url");
                }}
                key={state.websiteOption}
                defaultValue={form.url!}
                onBlur={() => state.validateInput("url", "url")}
                error={state.inputErrors.url.isError}
                helperText={
                  state.inputErrors.url.isError
                    ? translate(state.inputErrors.url.text)
                    : ""
                }
              />
            </SecondInputContainerEl>
          )}
        </BoxRowEl>

        {websiteOption === "in_dev" && (
          <BoxRowEl>
            <Box marginBottomL>
              <Dropdown
                items={webInDevelopmentOptions}
                initialSelectedItem={webInDevelopmentOptions.find(
                  (option) => option.id === linksOption
                )}
                label={translate("business_info.label.website_in_dev")}
                placeholder={translate(
                  "business_info.placeholder.website_in_dev"
                )}
                onChange={(event: any) => {
                  state.resetMarketplaceSocialMedia();
                  state.linksOption = event.id;
                  state.inputErrors.in_dev_url = {
                    isError: false,
                    text: "",
                  };
                }}
                size="m"
              />
            </Box>

            {linksOption !== "" && (
              <SecondInputContainerEl
                marginBottomL={!state.inputErrors.in_dev_url.isError}
              >
                <Input
                  placeholder={getLinkOptionPlaceholder()}
                  type="url"
                  key={linksOption}
                  onChange={(event) => {
                    setValue(linksOption as any, event.target.value);
                    state.inputErrors.in_dev_url.isError &&
                      state.validateInput("in_dev_url", linksOption as any);
                  }}
                  defaultValue={form[linksOption]!}
                  onBlur={() =>
                    state.validateInput("in_dev_url", linksOption as any)
                  }
                  error={state.inputErrors.in_dev_url.isError}
                  helperText={
                    state.inputErrors.in_dev_url.isError
                      ? translate(state.inputErrors.in_dev_url.text)
                      : ""
                  }
                  required
                />
              </SecondInputContainerEl>
            )}
          </BoxRowEl>
        )}

        {websiteOption === "no" && (
          <BoxRowEl>
            <Box marginBottomL>
              <Dropdown
                items={linkOptions}
                initialSelectedItem={linkOptions.find(
                  (option) => option.id === linksOption
                )}
                onChange={(event: any) => {
                  state.linksOption = event.id;
                  state.resetMarketplaceSocialMedia();
                  state.inputErrors.no_web_url = {
                    isError: false,
                    text: "",
                  };
                }}
                label={translate("business_info.label.no_website")}
                placeholder={translate("business_info.placeholder.no_website")}
                size="m"
              />
            </Box>
            {linksOption !== "" && (
              <SecondInputContainerEl
                marginBottomL={!state.inputErrors.no_web_url.isError}
              >
                <Input
                  placeholder={getLinkOptionPlaceholder()}
                  type="url"
                  key={linksOption}
                  onChange={(event) => {
                    setValue(linksOption as any, event.target.value);
                    state.inputErrors.no_web_url.isError &&
                      state.validateInput("no_web_url", linksOption as any);
                  }}
                  defaultValue={form[linksOption]!}
                  onBlur={() =>
                    state.validateInput("no_web_url", linksOption as any)
                  }
                  error={state.inputErrors.no_web_url.isError}
                  helperText={
                    state.inputErrors.no_web_url.isError
                      ? translate(state.inputErrors.no_web_url.text)
                      : ""
                  }
                />
              </SecondInputContainerEl>
            )}
          </BoxRowEl>
        )}

        {isShowAdditionalInfo && (
          <>
            <LabelEl htmlFor="textarea-additional-info">
              {translate("business_info.additional_info.label")}
            </LabelEl>
            <TextFieldContainerEl>
              <TextField
                multiline
                stretch
                placeholder={translate(
                  "business_info.additional_info.placeholder"
                )}
                extent="high"
                id="textarea-additional-info"
                onChange={(event: any) => setValue("notes", event.target.value)}
                defaultValue={form.notes || outlet.json?.notes}
              />
            </TextFieldContainerEl>
          </>
        )}

        {isShowWebGuideText && (
          <AdditionalInfoContainerEl>
            <Text fontSize="medium" marginBottomL as="span">
              {translate("business_info.additional_info.text")[0]}
            </Text>

            <Text
              color="primary"
              fontSize="medium"
              fontWeight="demi"
              marginBottomL
              as="span"
              onClick={() => setShowModal(true)}
              style={{
                cursor: "pointer",
              }}
            >
              {translate("business_info.additional_info.text")[1]}
            </Text>
            <Text fontSize="medium" marginBottomL as="span">
              {translate("business_info.additional_info.text")[2]}
            </Text>
          </AdditionalInfoContainerEl>
        )}

        <Breakpoint medium up>
          <BoldDividerEl />
        </Breakpoint>
        <Box flex justifyBetween marginTopXxl>
          <Link
            to="owner-info"
            onClick={() =>
              eventDispatcher.publish({ action: "ob_business_info_back" })
            }
          >
            <Button outline style={{ minWidth: "144px" }}>
              {translate("business_info.cta.back")}
            </Button>
          </Link>
          <Button
            style={{ minWidth: "144px" }}
            disabled={!state.isValid(isCorporate, isCardPayment)}
            type="submit"
          >
            {translate("business_info.cta.continue")}
          </Button>
        </Box>
      </Box>
      <LoadingOverlay show={store?.loadingState?.has.updateOutlet} />
      <ModalManager
        showModal={showModal}
        onClose={() => setShowModal(false)}
        allowFullScreen
      >
        <TooltipModal />
      </ModalManager>
    </>
  );
});
